import React, { useCallback, useMemo } from 'react'

import { ActionsProvider } from '../../../providers/ActionsProvider/ActionsProvider'
import { TooltipProvider } from '../../../providers/TooltipProvider/TooltipProvider'
import { useModal } from '../../../providers/ModalProvider/ModalProvider'
import { Confirm, SelectGASeats } from '..'
import { SeatMap } from '../../SeatMap'
import {
  getVenueSeatScale,
  isNmsuStyleSeatmapVenue,
} from '../../../utilities/frontendSeatmapSizing'
import {
  countSelectedSeats,
  countSelectedSeatsExcludingGaSection,
  formatHoldNames,
  getAvailableGaPackageSeatIds,
  getHoldLabel,
  getSeatMapValue,
  normalizeSeatId,
  partitionSeatClicks,
  sectionHasPackageGaInventory,
} from './relocatePackageSeatsMapHelpers'

const SELECTED_COLOR = '#141416'
const SOLD_COLOR = '#45B26B'
const RESERVED_COLOR = '#ee00f3'
const INCLUDED_COLOR = '#FFA500'
const HOLD_COLOR = '#EF466F'
const DEFAULT_COLOR = '#c6c8cc'
const MAX_SECTION_MULTI_SELECT = 500

const getAllSeatsInSeatedSection = (section, rows = {}, seats = {}) => {
  return Object.values(rows)
    .filter((row) => row.sectionId === section.sectionId)
    .flatMap((row) => row.seats || [])
    .filter((seatId) => Boolean(seats[seatId]))
}

export default function RelocatePackageSeatsMap({
  data,
  background,
  eventPackage,
  selectedSeatIds,
  setSelectedSeatIds,
  soldMap,
  reservedMap,
  holdMap,
  maxSelectable,
  restrictToPackageInventory = true,
}) {
  const { openModal, closeModal } = useModal()
  const venue =
    eventPackage?.venue ||
    eventPackage?.events?.[0]?.venue ||
    eventPackage?.event?.venue
  const venueSlug =
    venue?.slug || eventPackage?.venueSlug || eventPackage?.venue_slug
  const venueName =
    venue?.name || eventPackage?.venueName || eventPackage?.venue_name
  const useFrontendSeatSizing = isNmsuStyleSeatmapVenue(venueSlug)
  const seatScale = getVenueSeatScale({ venueName, venueSlug })
  const packageSeatIdSet = useMemo(
    () =>
      new Set(
        (eventPackage?.package_tickets || [])
          .map((ticket) => normalizeSeatId(ticket?.seatId ?? ticket?.seat_id))
          .filter(Boolean)
      ),
    [eventPackage?.package_tickets]
  )

  const getSeatState = useCallback(
    (seatId) => {
      const normalizedSeatId = normalizeSeatId(seatId)
      if (
        restrictToPackageInventory &&
        !packageSeatIdSet.has(normalizedSeatId)
      ) {
        return { disabled: true, color: DEFAULT_COLOR }
      }
      if (getSeatMapValue(reservedMap, seatId))
        return { disabled: true, color: RESERVED_COLOR }
      if (getSeatMapValue(soldMap, seatId))
        return { disabled: true, color: SOLD_COLOR }
      if (
        selectedSeatIds?.seats?.[normalizedSeatId] === normalizedSeatId ||
        selectedSeatIds?.seats?.[seatId] === seatId
      ) {
        return { disabled: false, color: SELECTED_COLOR }
      }
      if (getSeatMapValue(holdMap, seatId)) {
        return { disabled: false, color: HOLD_COLOR }
      }
      return { disabled: false, color: DEFAULT_COLOR }
    },
    [
      packageSeatIdSet,
      reservedMap,
      soldMap,
      holdMap,
      selectedSeatIds,
      restrictToPackageInventory,
    ]
  )

  const openGAModal = useCallback(
    (section) => {
      const availableSeatIds = getAvailableGaPackageSeatIds(
        eventPackage,
        section?.sectionId,
        soldMap,
        reservedMap
      )
      const nonHeldSeatIds = availableSeatIds.filter(
        (seatId) => !getSeatMapValue(holdMap, seatId)
      )
      const heldSeatIds = availableSeatIds.filter((seatId) =>
        getSeatMapValue(holdMap, seatId)
      )
      const remainingSlots = Math.max(
        0,
        maxSelectable -
          countSelectedSeatsExcludingGaSection(
            selectedSeatIds,
            section?.sectionId
          )
      )
      // Prefer open seats so a small quantity does not consume holds first.
      // Cap the assignable pool to remaining slots so SelectGASeats cannot
      // exceed the number of seats being added or relocated.
      const seatsToAssignFrom = [...nonHeldSeatIds, ...heldSeatIds].slice(
        0,
        remainingSlots
      )

      openModal(
        SelectGASeats,
        {
          setSelectedSeatIds,
          section,
          seatsToAssignFrom,
          holdMap,
          mode: 'assign',
          currentSeatIds: selectedSeatIds?.gaSeats?.[section?.sectionId] || [],
          // Keep the host modal open underneath this picker.
          dismissMode: 'top',
        },
        { className: '', contentClassName: '' },
        { replace: false }
      )
    },
    [
      eventPackage,
      holdMap,
      maxSelectable,
      openModal,
      reservedMap,
      selectedSeatIds,
      setSelectedSeatIds,
      soldMap,
    ]
  )

  const getSectionState = useCallback(
    (section) => {
      if (section?.zoomable) {
        return { disabled: false, color: DEFAULT_COLOR }
      }

      // GA destination sections must stay selectable when they belong to the
      // package — Relocate previously disabled all non-zoomable sections.
      if (
        restrictToPackageInventory &&
        !sectionHasPackageGaInventory(eventPackage, section?.sectionId)
      ) {
        return { disabled: true, color: DEFAULT_COLOR }
      }

      if (selectedSeatIds?.gaSeats?.[section?.sectionId]?.length) {
        return { disabled: false, color: SELECTED_COLOR }
      }

      const availableSeatIds = getAvailableGaPackageSeatIds(
        eventPackage,
        section?.sectionId,
        soldMap,
        reservedMap
      )
      if (availableSeatIds.length === 0) {
        return { disabled: false, color: SOLD_COLOR }
      }

      const heldSeatCount = availableSeatIds.filter((seatId) =>
        getSeatMapValue(holdMap, seatId)
      ).length
      if (heldSeatCount === availableSeatIds.length) {
        return { disabled: false, color: HOLD_COLOR }
      }

      return { disabled: false, color: INCLUDED_COLOR }
    },
    [
      eventPackage,
      holdMap,
      reservedMap,
      restrictToPackageInventory,
      selectedSeatIds,
      soldMap,
    ]
  )

  const applySeatIds = useCallback(
    (ids, { onlyAdd = false } = {}) => {
      if (!ids?.length) return

      setSelectedSeatIds((prevSelected) => {
        const next = { ...(prevSelected || {}) }
        const seats = { ...(next.seats || {}) }
        let selectedCount = countSelectedSeats({ ...next, seats })

        ids.forEach((id) => {
          const normalizedId = normalizeSeatId(id)
          if (!normalizedId || getSeatState(id).disabled) return

          if (seats[normalizedId]) {
            if (onlyAdd) return
            delete seats[normalizedId]
            selectedCount = Math.max(0, selectedCount - 1)
            return
          }

          if (selectedCount >= maxSelectable) return
          seats[normalizedId] = normalizedId
          selectedCount += 1
        })

        if (Object.keys(seats).length > 0) next.seats = seats
        else delete next.seats

        return next
      })
    },
    [getSeatState, maxSelectable, setSelectedSeatIds]
  )

  const confirmHeldSeatSelection = useCallback(
    (seatIds, holdLabels) => {
      const holdNames = formatHoldNames(holdLabels)
      const isSingle = seatIds.length === 1
      const description = holdNames
        ? isSingle
          ? `This seat is on hold for "${holdNames}". Would you like to continue and select it?`
          : `Some of these seats are on hold for "${holdNames}". Would you like to continue and select them?`
        : isSingle
          ? 'This seat is currently on hold. Would you like to continue and select it?'
          : 'Some of these seats are currently on hold. Would you like to continue and select them?'

      openModal(
        Confirm,
        {
          title: isSingle ? 'Seat on hold' : 'Seats on hold',
          description,
          confirmLabel: 'Yes',
          onConfirm: () => {
            applySeatIds(seatIds, { onlyAdd: true })
            closeModal()
          },
          onClose: closeModal,
        },
        { className: 'modal-md', contentClassName: '' },
        { replace: false }
      )
    },
    [applySeatIds, closeModal, openModal]
  )

  const selectRegularSeats = useCallback(
    (seatId, seatIdArray) => {
      const idsToToggle = seatId
        ? [seatId]
        : Array.isArray(seatIdArray)
          ? seatIdArray.slice(0, MAX_SECTION_MULTI_SELECT)
          : []

      if (!idsToToggle.length) return

      const normalizedIds = idsToToggle
        .map((id) => normalizeSeatId(id))
        .filter(Boolean)
      const { toToggle, heldToAdd, holdLabels } = partitionSeatClicks(
        normalizedIds,
        {
          isDisabled: (id) => getSeatState(id).disabled,
          isSelected: (id) =>
            selectedSeatIds?.seats?.[id] === id ||
            selectedSeatIds?.seats?.[normalizeSeatId(id)] ===
              normalizeSeatId(id),
          getHoldInfo: (id) => getSeatMapValue(holdMap, id),
          selectedCount: countSelectedSeats(selectedSeatIds),
          maxSelectable,
        }
      )

      if (toToggle.length > 0) applySeatIds(toToggle)
      if (heldToAdd.length > 0) confirmHeldSeatSelection(heldToAdd, holdLabels)
    },
    [
      applySeatIds,
      confirmHeldSeatSelection,
      getSeatState,
      holdMap,
      maxSelectable,
      selectedSeatIds,
    ]
  )

  const handleSectionSelect = useCallback(
    (section) => {
      if (section?.zoomable) {
        const seatIds = getAllSeatsInSeatedSection(
          section,
          data?.rows,
          data?.seats
        )
        selectRegularSeats(undefined, seatIds)
        return
      }

      if (
        restrictToPackageInventory &&
        !sectionHasPackageGaInventory(eventPackage, section?.sectionId)
      ) {
        return
      }

      openGAModal(section)
    },
    [
      data?.rows,
      data?.seats,
      eventPackage,
      openGAModal,
      restrictToPackageInventory,
      selectRegularSeats,
    ]
  )

  const getSeatTooltipDetails = useCallback(
    (seatId) => {
      const seatIdentity =
        getSeatMapValue(reservedMap, seatId) || getSeatMapValue(soldMap, seatId)
      if (seatIdentity && seatIdentity !== true) return seatIdentity

      const holdLabel = getHoldLabel(getSeatMapValue(holdMap, seatId))
      if (holdLabel) return { holdName: holdLabel }

      return null
    },
    [holdMap, reservedMap, soldMap]
  )

  const legendItems = [
    { label: 'Sold', className: 'sold' },
    { label: 'Reserved', className: 'reserved' },
    { label: 'Selected', className: 'selected' },
    ...(holdMap?.size ? [{ label: 'Hold', className: 'hold' }] : []),
    { label: 'Unselected', className: 'unselected' },
  ]

  return (
    <div style={{ width: '100%', height: '460px', overflow: 'hidden' }}>
      <TooltipProvider>
        <ActionsProvider canEdit={true} legendItems={legendItems}>
          <SeatMap
            data={data}
            background={background}
            isMapDisabled={false}
            useFrontendSeatSizing={useFrontendSeatSizing}
            seatScale={seatScale}
            selectRegularSeats={selectRegularSeats}
            handleSectionSelect={handleSectionSelect}
            getSectionState={getSectionState}
            getSeatState={getSeatState}
            getSeatTooltipDetails={getSeatTooltipDetails}
            displayInventoryInfo={false}
            deriveInventoryInfoFrom={[]}
          />
        </ActionsProvider>
      </TooltipProvider>
    </div>
  )
}
